import { useMemo, useState, useCallback, useEffect } from "react";
import { useQuery } from "react-query";
import { useSimulator } from "./SimulatorWrapper";
import { useTrainingSessionEventApi } from "@/v2/entities/training-session-event/TrainingSessionEvent.api";
import {
  TrainingSessionEventType,
  TrainingSessionEvent,
  GetTrainingSessionEventsRequest,
} from "@/v2/entities/training-session-event/TrainingSessionEvent.model";
import { Topic } from "@/v2/entities/topic/Topic.model";

export type ActivityLogFilters = {
  selectedReleaseId: number | null;
  selectedFilters: Array<{ type: "topic" | "type"; value: string }>;
  sortOrder: "newest" | "oldest";
  searchQuery: string;
};

export type ActivityLog = {
  isLoading: boolean;
  activities: ActivityItem[];
  allActivities: ActivityItem[];
  sessionsCount: number | null;
  goodSessionCount: number | null;
  badFeedbackCount: number | null;
  isThereNextPage: boolean;
  totalCount: number;
  loadMore: () => void;
  isLoadingMore: boolean;
};

export const activityType = [
  "General",
  "Instruction",
  "Brief",
  "Prerequisite",
  "Session",
] as const;
export type ActivityType = (typeof activityType)[number];

export const sessionTag = [
  "Good Feedback",
  "Bad Feedback",
  "Improved",
  "File",
  "From Ticket",
  "From Conversation",
] as const;
export type SessionTag = (typeof sessionTag)[number];

export type ActivityItem = {
  type: ActivityType;
  content: string | { comment: string };
  topic: string | null;
  timestamp: string;
  id: string;
  releaseId: number;
  version: number;
  versionLabel: string;
  sourceType?: "ticketing" | "knowledge_base";
  action: string;
  sessionTags?: SessionTag[];
  conversationId?: string;
};

export const getActivityContent = (activity: ActivityItem): string => {
  return typeof activity.content === "string"
    ? activity.content
    : activity.content?.comment
    ? activity.content.comment
    : typeof activity.content === "object"
    ? JSON.stringify(activity.content)
    : "";
};

// Mapping from TrainingSessionEventType to ActivityType
const eventTypeToActivityType = (
  eventType: TrainingSessionEventType
): ActivityType => {
  switch (eventType) {
    case TrainingSessionEventType.CREATE_INSTRUCTION:
    case TrainingSessionEventType.DELETE_INSTRUCTION:
      return "Instruction";
    case TrainingSessionEventType.EDIT_BRIEF:
      return "Brief";
    case TrainingSessionEventType.CREATE_PREREQUISITE:
    case TrainingSessionEventType.DELETE_PREREQUISITE:
      return "Prerequisite";
    case TrainingSessionEventType.ADMIN_MESSAGE:
    case TrainingSessionEventType.AI_MESSAGE:
    case TrainingSessionEventType.MESSAGE_REGENERATED:
    case TrainingSessionEventType.MESSAGE_GOOD_RESPONSE:
    case TrainingSessionEventType.MESSAGE_BAD_RESPONSE:
    case TrainingSessionEventType.MESSAGE_GOOD_RESPONSE_UNDO:
    case TrainingSessionEventType.TEST_INTERACTION_IN_TRAINING:
    case TrainingSessionEventType.TEST_INTERACTION:
      return "Session";
    default:
      return "General";
  }
};

// Get session tags based on event type and value
const getSessionTags = (
  eventType: TrainingSessionEventType,
  value: Record<string, any>
): SessionTag[] => {
  const tags: SessionTag[] = [];

  switch (eventType) {
    case TrainingSessionEventType.MESSAGE_GOOD_RESPONSE:
      tags.push("Good Feedback");
      break;
    case TrainingSessionEventType.MESSAGE_BAD_RESPONSE:
      tags.push("Bad Feedback");
      break;
    case TrainingSessionEventType.IMPROVE_SUBMITTED:
      tags.push("Improved");
      break;
    case TrainingSessionEventType.UPLOAD_FILE:
      tags.push("File");
      break;
    case TrainingSessionEventType.TEST_INTERACTION:
      if (value.sourceType === "ticketing") {
        tags.push("From Ticket");
      } else if (value.sourceType === "knowledge_base") {
        tags.push("From Conversation");
      }
      break;
  }

  return tags;
};

// Get content from event value
const getEventContent = (
  eventType: TrainingSessionEventType,
  value: Record<string, any>
): string => {
  switch (eventType) {
    case TrainingSessionEventType.CREATE_INSTRUCTION:
    case TrainingSessionEventType.DELETE_INSTRUCTION:
      return value.content || value.instructionContent || "Instruction";
    case TrainingSessionEventType.EDIT_BRIEF:
      return `Topic brief: ${value.brief || value.content || ""}`;
    case TrainingSessionEventType.CREATE_PREREQUISITE:
    case TrainingSessionEventType.DELETE_PREREQUISITE:
      return value.content || value.prerequisiteContent || "Prerequisite";
    case TrainingSessionEventType.ADMIN_MESSAGE:
    case TrainingSessionEventType.AI_MESSAGE:
      return value.message || value.content || "Message";
    case TrainingSessionEventType.EDIT_CONFIGURATION:
      return `Configuration updated: ${value.configType || "General"}`;
    case TrainingSessionEventType.MANUAL_ENABLE_TOPIC:
      return "Topic enabled";
    case TrainingSessionEventType.MANUAL_DISABLE_TOPIC:
      return "Topic disabled";
    case TrainingSessionEventType.UPLOAD_FILE:
      return `File uploaded: ${value.fileName || "Unknown file"}`;
    case TrainingSessionEventType.PUBLISH_RELEASE:
      return "Release published";
    case TrainingSessionEventType.IMPROVE_SUGGESTED:
    case TrainingSessionEventType.IMPROVE_SUBMITTED:
      return value.suggestion || value.improvement || "Improvement";
    default:
      return value.content || value.message || "Activity";
  }
};

// Get action from event type
const getEventAction = (eventType: TrainingSessionEventType): string => {
  switch (eventType) {
    case TrainingSessionEventType.CREATE_INSTRUCTION:
    case TrainingSessionEventType.CREATE_PREREQUISITE:
    case TrainingSessionEventType.UPLOAD_FILE:
    case TrainingSessionEventType.PUBLISH_RELEASE:
    case TrainingSessionEventType.MANUAL_ENABLE_TOPIC:
      return "added";
    case TrainingSessionEventType.DELETE_INSTRUCTION:
    case TrainingSessionEventType.DELETE_PREREQUISITE:
    case TrainingSessionEventType.MANUAL_DISABLE_TOPIC:
      return "deleted";
    case TrainingSessionEventType.EDIT_BRIEF:
    case TrainingSessionEventType.EDIT_CONFIGURATION:
    case TrainingSessionEventType.ADMIN_MESSAGE:
    case TrainingSessionEventType.AI_MESSAGE:
    case TrainingSessionEventType.MESSAGE_REGENERATED:
    case TrainingSessionEventType.MESSAGE_GOOD_RESPONSE:
    case TrainingSessionEventType.MESSAGE_BAD_RESPONSE:
    case TrainingSessionEventType.MESSAGE_GOOD_RESPONSE_UNDO:
    case TrainingSessionEventType.TEST_INTERACTION_IN_TRAINING:
    case TrainingSessionEventType.TEST_INTERACTION:
    case TrainingSessionEventType.IMPROVE_SUGGESTED:
    case TrainingSessionEventType.IMPROVE_SUBMITTED:
      return "updated";
    default:
      return "modified";
  }
};

// Group events by session identifier
const groupEventsBySession = (
  events: TrainingSessionEvent[]
): TrainingSessionEvent[][] => {
  const groups: { [key: string]: TrainingSessionEvent[] } = {};

  events.forEach((event) => {
    // Create a session key based on conversation/release context
    let sessionKey: string;

    // Only group events that are part of the same conversation or session
    if (event.releaseConversationId) {
      // Group by release conversation - these are part of the same conversation
      sessionKey = `conversation-${event.releaseConversationId}`;
    } else if (
      event.type === TrainingSessionEventType.ADMIN_MESSAGE ||
      event.type === TrainingSessionEventType.AI_MESSAGE ||
      event.type === TrainingSessionEventType.TEST_INTERACTION ||
      event.type === TrainingSessionEventType.TEST_INTERACTION_IN_TRAINING ||
      event.type === TrainingSessionEventType.MESSAGE_GOOD_RESPONSE ||
      event.type === TrainingSessionEventType.MESSAGE_BAD_RESPONSE ||
      event.type === TrainingSessionEventType.MESSAGE_REGENERATED ||
      event.type === TrainingSessionEventType.IMPROVE_SUBMITTED
    ) {
      // Group session-related events by topic and release
      if (event.topicId && event.releaseId) {
        sessionKey = `session-topic-${event.topicId}-release-${event.releaseId}`;
      } else if (event.releaseId) {
        sessionKey = `session-release-${event.releaseId}`;
      } else {
        sessionKey = `event-${event.id}`;
      }
    } else {
      // For non-session events, display each event individually
      sessionKey = `event-${event.id}`;
    }

    if (!groups[sessionKey]) {
      groups[sessionKey] = [];
    }
    groups[sessionKey].push(event);
  });

  return Object.values(groups);
};

const transformGroupedEventsToActivity = (
  events: TrainingSessionEvent[],
  topics: Topic[] | null,
  releasesWithVersions: { id: number; version: number }[] | null
): ActivityItem => {
  const getTopicName = (topicId: number | null): string | null => {
    if (!topicId || !topics || !Array.isArray(topics)) return null;
    const topic = topics.find((t) => t.id === topicId);
    return topic?.topic || null;
  };

  const getVersionInfo = (releaseId: number) => {
    if (!releasesWithVersions) return { version: 1, versionLabel: "Version 1" };
    const release = releasesWithVersions.find((r) => r.id === releaseId);
    return {
      version: release?.version || 1,
      versionLabel: `Version ${release?.version || 1}`,
    };
  };

  // Sort events by timestamp to get the earliest one
  const sortedEvents = events.sort(
    (a, b) =>
      new Date(a.eventCreatedAt || a.createdAt).getTime() -
      new Date(b.eventCreatedAt || b.createdAt).getTime()
  );

  const firstEvent = sortedEvents[0];
  const { version, versionLabel } = getVersionInfo(firstEvent.releaseId);

  // Determine the main activity type and content
  const sessionEvents = events.filter(
    (event) =>
      event.type === TrainingSessionEventType.ADMIN_MESSAGE ||
      event.type === TrainingSessionEventType.AI_MESSAGE ||
      event.type === TrainingSessionEventType.TEST_INTERACTION ||
      event.type === TrainingSessionEventType.TEST_INTERACTION_IN_TRAINING ||
      event.type === TrainingSessionEventType.MESSAGE_GOOD_RESPONSE ||
      event.type === TrainingSessionEventType.MESSAGE_BAD_RESPONSE ||
      event.type === TrainingSessionEventType.MESSAGE_REGENERATED ||
      event.type === TrainingSessionEventType.IMPROVE_SUBMITTED
  );

  let activityType: ActivityType = "General";
  let content = "";
  let sessionTags: SessionTag[] = [];

  if (sessionEvents.length > 0) {
    activityType = "Session";
    // Use the first user message as content
    const userMessage = sessionEvents.find(
      (event) => event.type === TrainingSessionEventType.ADMIN_MESSAGE
    );
    content = userMessage
      ? getEventContent(userMessage.type, userMessage.value)
      : "Session";

    // Collect all session tags from the group
    sessionEvents.forEach((event) => {
      const tags = getSessionTags(event.type, event.value);
      sessionTags.push(...tags);
    });
    // Remove duplicates
    sessionTags = [...new Set(sessionTags)];
  } else {
    // For non-session events, use the first event
    activityType = eventTypeToActivityType(firstEvent.type);
    content = getEventContent(firstEvent.type, firstEvent.value);
    sessionTags = getSessionTags(firstEvent.type, firstEvent.value);
  }

  return {
    type: activityType,
    content,
    topic: getTopicName(firstEvent.topicId || null),
    timestamp: firstEvent.eventCreatedAt || firstEvent.createdAt,
    id: firstEvent.releaseConversationId
      ? `conversation-${firstEvent.releaseConversationId}`
      : events.length === 1
      ? firstEvent.id
      : firstEvent.topicId && firstEvent.releaseId
      ? `session-topic-${firstEvent.topicId}-release-${firstEvent.releaseId}`
      : firstEvent.releaseId
      ? `session-release-${firstEvent.releaseId}`
      : `${firstEvent.id}-group-${events.length}`,
    releaseId: firstEvent.releaseId,
    version,
    versionLabel,
    sourceType: firstEvent.value?.sourceType,
    action: "updated", // Sessions are typically updated over time
    sessionTags,
    conversationId: firstEvent.releaseConversationId?.toString(),
  };
};

// Build request function to separate concerns
const buildRequest = (
  simulatorId: number | undefined,
  selectedReleaseId: number | null,
  selectedFilters: Array<{ type: "topic" | "type"; value: string }>,
  sortOrder: "newest" | "oldest",
  topics: Topic[] | null,
  currentPage: number
): GetTrainingSessionEventsRequest => {
  const request: GetTrainingSessionEventsRequest = {
    page: currentPage,
  };

  if (simulatorId) {
    request.simulatorId = simulatorId;
  }

  if (selectedReleaseId) {
    request.releaseIds = [selectedReleaseId];
  }

  // Add topic filters to API call
  const topicFilters = selectedFilters
    .filter((f) => f.type === "topic")
    .map((f) => f.value);
  if (topicFilters.length > 0 && topics) {
    const topicIds = topics
      .filter((topic) => topicFilters.includes(topic.topic))
      .map((topic) => topic.id);
    if (topicIds.length > 0) {
      request.topicIds = topicIds;
    }
  }

  // Add activity type filters to API call
  const typeFilters = selectedFilters
    .filter((f) => f.type === "type")
    .map((f) => f.value);
  if (typeFilters.length > 0) {
    const eventTypes: TrainingSessionEventType[] = [];

    typeFilters.forEach((type) => {
      switch (type) {
        case "Instruction":
          eventTypes.push(
            TrainingSessionEventType.CREATE_INSTRUCTION,
            TrainingSessionEventType.DELETE_INSTRUCTION
          );
          break;
        case "Brief":
          eventTypes.push(TrainingSessionEventType.EDIT_BRIEF);
          break;
        case "Prerequisite":
          eventTypes.push(
            TrainingSessionEventType.CREATE_PREREQUISITE,
            TrainingSessionEventType.DELETE_PREREQUISITE
          );
          break;
        case "Session":
          eventTypes.push(
            TrainingSessionEventType.TEST_INTERACTION,
            TrainingSessionEventType.TEST_INTERACTION_IN_TRAINING,
            TrainingSessionEventType.ADMIN_MESSAGE,
            TrainingSessionEventType.AI_MESSAGE,
            TrainingSessionEventType.MESSAGE_GOOD_RESPONSE,
            TrainingSessionEventType.MESSAGE_BAD_RESPONSE,
            TrainingSessionEventType.MESSAGE_REGENERATED,
            TrainingSessionEventType.IMPROVE_SUBMITTED
          );
          break;
        case "General":
          eventTypes.push(
            TrainingSessionEventType.EDIT_CONFIGURATION,
            TrainingSessionEventType.MANUAL_ENABLE_TOPIC,
            TrainingSessionEventType.MANUAL_DISABLE_TOPIC,
            TrainingSessionEventType.UPLOAD_FILE,
            TrainingSessionEventType.PUBLISH_RELEASE
          );
          break;
      }
    });

    if (eventTypes.length > 0) {
      request.eventTypes = eventTypes;
    }
  }

  return request;
};

const filterActivityLog = (
  activities: ActivityItem[],
  filters: ActivityLogFilters
): ActivityItem[] => {
  let filteredActivities = activities || [];

  // Client-side search filtering (since API doesn't support text search)
  if (filters.searchQuery) {
    const query = filters.searchQuery.toLowerCase();
    filteredActivities = filteredActivities.filter(
      (activity) =>
        getActivityContent(activity).toLowerCase().includes(query) ||
        activity.topic?.toLowerCase().includes(query) ||
        activity.type.toLowerCase().includes(query)
    );
  }

  // Client-side release filtering (as backup, though it's also handled at API level)
  if (filters.selectedReleaseId) {
    filteredActivities = filteredActivities.filter(
      (activity) => activity.releaseId === filters.selectedReleaseId
    );
  }

  // Create a copy before sorting to avoid mutation
  return [...filteredActivities].sort((a, b) => {
    if (a.releaseId !== b.releaseId) {
      return filters.sortOrder === "newest"
        ? b.releaseId - a.releaseId
        : a.releaseId - b.releaseId;
    }

    const timeA = new Date(a.timestamp).getTime();
    const timeB = new Date(b.timestamp).getTime();
    return filters.sortOrder === "newest" ? timeB - timeA : timeA - timeB;
  });
};

export const useActivityLog = ({
  filters,
}: {
  filters: ActivityLogFilters;
}): ActivityLog => {
  const { simulator, releasesWithVersions, topics } = useSimulator();
  const trainingSessionEventApi = useTrainingSessionEventApi();
  const [currentPage, setCurrentPage] = useState(1);
  const [allActivities, setAllActivities] = useState<ActivityItem[]>([]);
  const [isThereNextPage, setIsThereNextPage] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [sessionCounts, setSessionCounts] = useState<{
    sessionsCount: number | null;
    goodSessionCount: number | null;
    badFeedbackCount: number | null;
  }>({
    sessionsCount: null,
    goodSessionCount: null,
    badFeedbackCount: null,
  });

  // Destructure filters to get stable primitive dependencies
  const { selectedReleaseId, selectedFilters, sortOrder, searchQuery } =
    filters;

  const eventFilters = useMemo(() => {
    return buildRequest(
      simulator?.id,
      selectedReleaseId,
      selectedFilters,
      sortOrder,
      topics,
      currentPage
    );
  }, [
    simulator?.id,
    selectedReleaseId,
    JSON.stringify(selectedFilters), // Stringify to ensure stable reference
    sortOrder,
    topics,
    currentPage,
  ]);

  const eventsQuery = useQuery({
    queryKey: ["training-session-events", eventFilters],
    queryFn: () => trainingSessionEventApi.getEvents(eventFilters),
    enabled: !!simulator?.id,
    onSuccess: (data) => {
      if (data) {
        const uniqueEvents = data.events.filter(
          (event, index, self) =>
            index === self.findIndex((e) => e.id === event.id)
        );

        const groupedEvents = groupEventsBySession(uniqueEvents);
        const transformedActivities = groupedEvents.map((group) =>
          transformGroupedEventsToActivity(group, topics, releasesWithVersions)
        );

        if (currentPage === 1) {
          setAllActivities(transformedActivities);
        } else {
          setAllActivities((prev) => {
            // Deduplicate activities by ID to ensure each event is displayed only once
            const existingIds = new Set(prev.map((activity) => activity.id));
            const newActivities = transformedActivities.filter(
              (activity) => !existingIds.has(activity.id)
            );
            return [...prev, ...newActivities];
          });
        }

        setIsThereNextPage(data.isThereNextPage);
        setTotalCount(data.totalCount);
        setSessionCounts({
          sessionsCount: data.sessionsCount,
          goodSessionCount: data.goodSessionCount,
          badFeedbackCount: data.badFeedbackCount,
        });
      }
    },
  });

  const activities = useMemo(() => {
    const filteredActivities = filterActivityLog(allActivities, filters);
    return filteredActivities;
  }, [allActivities, filters]);

  const loadMore = useCallback(() => {
    if (isThereNextPage && !eventsQuery.isLoading) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [isThereNextPage, eventsQuery.isLoading]);

  useEffect(() => {
    setCurrentPage(1);
    setAllActivities([]);
    setIsThereNextPage(false);
    setTotalCount(0);
  }, [selectedReleaseId, sortOrder, JSON.stringify(selectedFilters)]);

  return {
    isLoading: eventsQuery.isLoading && currentPage === 1,
    activities,
    allActivities,
    sessionsCount: sessionCounts.sessionsCount,
    goodSessionCount: sessionCounts.goodSessionCount,
    badFeedbackCount: sessionCounts.badFeedbackCount,
    isThereNextPage,
    totalCount,
    loadMore,
    isLoadingMore: eventsQuery.isLoading && currentPage > 1,
  };
};
